//@flow
import React, { PureComponent } from 'react';
import * as Immutable from 'immutable';
import { themr } from 'react-css-themr';
import { connect } from 'react-redux';
import { Checkbox } from 'common/components/core/checkbox';
import classnames from 'classnames';
import { translate } from 'react-i18next';
import compose from 'recompose/compose';
import IntelliSortActions from 'common/redux/actions/intellisort';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import ToastActions from 'common/redux/actions/toast';
import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';
import TimeUtils from 'common/utils/TimeUtils';

import { Link } from 'react-router-dom';
import { FeedbackSwitch } from 'common/components/intellisort/IntelliSortFeedback';
import { IntelliSortFeedbackReason } from 'common/components/intellisort/IntelliSortFeedbackReason';
import { IntelliSortFeedbackZoneSelector } from 'common/components/intellisort/IntelliSortFeedbackZoneSelector';
import IntelliSortActionsBar from 'common/components/intellisort/IntelliSortActionsBar';
import {
    clientSelector,
    selectedDateSelector,
    activeTeamIdSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import IntelliSortSelectors from 'common/redux/selectors/intelliSort';
import {
    getEntitySettings,
    getAutoSortSettings
} from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { withRouter } from 'common/components/core/router';
import { withUnitConverter } from 'common/containers/intl';

import theme from 'common/entity/BulkEditContainer.scss';
import specificTheme from './IntelliSortTableContainer.scss';

import { Loader } from 'common/components/core/loader';
import { Button, ButtonTypes } from 'common/components/core/button';
import { Modal } from 'common/entity/generic/SingleEntityEditView';
import BulkEditConfirmationDialog from 'common/entity/generic/BulkEditConfirmationDialog';
import { ErrorIcon } from 'common/components/core/icon';
import PromiseUtils, { PromiseStatus } from 'common/utils/PromiseUtils';

import { dashboardColors } from 'common/enums/ColorEnum';
import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import ResponseLayerFilter from 'common/components/intellisort/ResponseLayerFilter';
import ResponseSourceFilter from 'common/components/intellisort/ResponseSourceFilter';
import FeedbackStatusFilter from 'common/components/intellisort/FeedbackStatusFilter';
import IntelliSortEmptyState from 'common/components/intellisort/IntelliSortEmptyState';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import { homebase as fetchHomebasesApi } from 'common/middleware/api';
import { Homebase } from 'common/models/internal/Homebase';

import type { IntelliSortRequest } from 'common/models/internal/IntelliSortRequest';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { TFunction } from 'react-i18next';

const ENTITY_TABLE_ID = 'IntelliSort';

const DEFAULT_COLUMNS = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick }
                }
            } = props;
            return (
                <Checkbox
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    className={theme.selectionCell}
                    theme={theme}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        frozen: true,
        disablePinControl: true,
        width: 40
    },
    {
        headerTextId: 'intelliSortTable.id',
        accessor: 'request.id',
        frozen: true,
        minWidth: 80,
        infixSearchable: true
    },
    {
        headerTextId: 'intelliSortTable.batchId',
        accessor: 'zoneBasedRoutingMetadata.batchId',
        minWidth: 120,
        infixSearchable: true,
        isHidden: props => props.isZoneBasedRoutingMetadataHidden
    },
    {
        headerTextId: 'intelliSortTable.address',
        accessor: 'request.address.formattedAddress',
        minWidth: 300,
        infixSearchable: true,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme }
                }
            } = props;
            return (
                <span className={theme.addressText}>
                    {original.request.address.formattedAddress}
                </span>
            );
        }
    },
    {
        headerTextId: 'intelliSortTable.routeId',
        accessor: 'response.route.id',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'intelliSortTable.accuracy',
        id: 'accuracy',
        minWidth: 150,
        sortable: false,
        filterable: true,
        fieldId: 'accuracy',
        FilterComponent: FeedbackStatusFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isFeedbackActive, theme, handleFeedback, t }
                }
            } = props;
            const feedbackStatus =
                original.feedback.zoneFeedback.feedbackStatus;
            let isVerified = false;
            let isIgnored = false;
            switch (feedbackStatus) {
                case 'ACCEPTED':
                    isVerified = true;
                    break;
                case 'REJECTED':
                    isIgnored = true;
            }

            return (
                <FeedbackSwitch
                    orderId={original.id}
                    isVerified={isVerified}
                    isIgnored={isIgnored}
                    verifyBtnTooltip={t('accurate')}
                    ignoreBtnTooltip={t('inaccurate')}
                    onChange={handleFeedback}
                    className={theme.cell}
                    isFeedbackActive={isFeedbackActive}
                />
            );
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('feedback.zoneFeedback.feedbackStatus')
                .withOperation(FilterOperation.Equals)
                .withValues(values);
        }
    },
    {
        headerTextId: 'intelliSortTable.allowedRouteIds',
        id: 'allowedRouteIds',
        accessor: 'allowedRouteIds',
        minWidth: 150,
        sortable: false,
        filterable: false,
        fieldId: 'routeId',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { data, isFeedbackActive, handleFeedback, t }
                }
            } = props;
            const routeId = original.feedback.zoneFeedback.routeId;
            const feedbackStatus =
                original.feedback.zoneFeedback.feedbackStatus;
            const versionedMapId = original.versionedMapIds.get(0);
            const allowedRouteIds = data.allowedRouteIds?.find(obj => {
                return obj.mapId === versionedMapId.mapId.mapId;
            });
            return (
                <IntelliSortFeedbackZoneSelector
                    onChange={handleFeedback}
                    allowedRouteIds={allowedRouteIds?.routeIds}
                    routeId={routeId}
                    feedbackStatus={feedbackStatus}
                    orderId={original.id}
                    isFeedbackActive={isFeedbackActive}
                    placeholder={t('routeIdPlaceholder')}
                />
            );
        }
    },
    {
        headerTextId: 'intelliSortTable.reason',
        id: 'reason',
        accessor: 'reason',
        minWidth: 200,
        sortable: false,
        filterable: false,
        fieldId: 'reason',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isFeedbackActive, handleFeedback, t }
                }
            } = props;
            const feedbackStatus =
                original.feedback.zoneFeedback.feedbackStatus;
            const cellText =
                original.feedback.zoneFeedback.reasons.size > 0
                    ? original.feedback.zoneFeedback.reasons.get(-1).message
                    : null;
            return (
                <IntelliSortFeedbackReason
                    orderId={original.id}
                    feedbackStatus={feedbackStatus}
                    placeholder={t('feedbackReasonPlaceholder')}
                    onSave={handleFeedback}
                    cellText={cellText}
                    isFeedbackActive={isFeedbackActive}
                />
            );
        }
    },
    {
        headerTextId: 'intelliSortTable.routeName',
        accessor: 'response.route.name',
        minWidth: 120,
        infixSearchable: true
    },
    {
        headerTextId: 'intelliSortTable.dropDate',
        accessor: 'date',
        minWidth: 120,
        infixSearchable: true,
        isHidden: props => props.isZoneBasedRoutingMetadataHidden
    },
    {
        headerTextId: 'intelliSortTable.pincode',
        accessor: 'request.address.pincode',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'intelliSortTable.city',
        accessor: 'request.address.city',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'intelliSortTable.country',
        accessor: 'request.address.countryCode',
        minWidth: 100,
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'intelliSortTable.locality',
        accessor: 'request.address.localityName',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'intelliSortTable.mapName',
        accessor: 'mapName',
        minWidth: 100,
        sortable: false,
        filterable: false,
        Cell: props => {
            const { original: intellisortRequest } = props;
            const versionedMapId = intellisortRequest.versionedMapIds.get(0);
            return (
                <Link
                    className={classnames(theme.cellText, theme.link)}
                    to={`/client/${versionedMapId.mapId.clientId}/entity/map/${versionedMapId.mapId.mapId}/version/${versionedMapId.version}`}
                    target="_blank"
                >
                    {versionedMapId.mapId.mapId} v{versionedMapId.version}
                </Link>
            );
        }
    },
    {
        headerTextId: 'intelliSortTable.mapId',
        accessor: 'mapId',
        minWidth: 100,
        sortable: false,
        filterable: false,
        show: false,
        Cell: props => {
            const { original } = props;
            const versionedMapId = original.versionedMapIds.get(0);
            return (
                <span className={theme.cellText}>
                    {versionedMapId.mapId.mapId}
                </span>
            );
        }
    },
    {
        headerTextId: 'intelliSortTable.mapVersion',
        accessor: 'mapVersion',
        minWidth: 100,
        sortable: false,
        filterable: false,
        show: false,
        Cell: props => {
            const { original } = props;
            const versionedMapId = original.versionedMapIds.get(0);
            return (
                <span className={theme.cellText}>{versionedMapId.version}</span>
            );
        }
    },
    {
        headerTextId: 'intelliSortTable.geocodedAddress',
        accessor: 'geocodedLocation.address.formattedAddress',
        minWidth: 200,
        infixSearchable: true,
        isHidden: props => {
            return !props.isGlobalPersonnel;
        },
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme }
                }
            } = props;
            return (
                <span className={theme.addressText}>
                    {original.geocodedLocation.address.formattedAddress}
                </span>
            );
        }
    },
    {
        headerTextId: 'intelliSortTable.geocodedLocality',
        accessor: 'geocodedLocation.address.localityName',
        minWidth: 100,
        infixSearchable: true,
        isHidden: props => {
            return !props.isGlobalPersonnel;
        }
    },
    {
        headerTextId: 'intelliSortTable.geocodedSubLocality',
        accessor: 'geocodedLocation.address.subLocalityName',
        minWidth: 100,
        infixSearchable: true,
        isHidden: props => {
            return !props.isGlobalPersonnel;
        }
    },
    {
        headerTextId: 'intelliSortTable.responseLayer',
        accessor: 'response.responseLayer',
        minWidth: 100,
        FilterComponent: ResponseLayerFilter,
        isHidden: props => {
            return !props.isGlobalPersonnel;
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('response.responseLayer')
                .withOperation(FilterOperation.Equals)
                .withValues(values);
        }
    },
    {
        headerTextId: 'intelliSortTable.responseSource',
        accessor: 'responseSource',
        minWidth: 100,
        show: false,
        FilterComponent: ResponseSourceFilter,
        isHidden: props => {
            return !props.isGlobalPersonnel;
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('responseSource')
                .withOperation(FilterOperation.Equals)
                .withValues(values);
        }
    },
    {
        headerTextId: 'intelliSortTable.date',
        accessor: 'date',
        minWidth: 100,
        infixSearchable: true,
        isHidden: props => !props.isZoneBasedRoutingMetadataHidden
    },
    {
        headerTextId: 'intelliSortTable.createdBy',
        accessor: 'feedback.auditMetadata.createdBy.id',
        minWidth: 100,
        sortable: false,
        filterable: false,
        show: false,
        isHidden: props => {
            return !props.isGlobalPersonnel;
        }
    },
    {
        headerTextId: 'intelliSortTable.updatedBy',
        accessor: 'feedback.auditMetadata.updatedBy.id',
        minWidth: 100,
        sortable: false,
        filterable: false,
        show: false,
        isHidden: props => {
            return !props.isGlobalPersonnel;
        }
    },
    {
        headerTextId: 'intelliSortTable.createdOn',
        accessor: 'feedback.auditMetadata.createdOn',
        minWidth: 100,
        sortable: false,
        filterable: false,
        show: false,
        dataType: CellDataTypes.DateTime,
        isHidden: props => {
            return !props.isGlobalPersonnel;
        }
    },
    {
        headerTextId: 'intelliSortTable.updatedOn',
        accessor: 'feedback.auditMetadata.updatedOn',
        minWidth: 100,
        sortable: false,
        filterable: false,
        show: false,
        dataType: CellDataTypes.DateTime,
        isHidden: props => {
            return !props.isGlobalPersonnel;
        }
    },
    {
        headerTextId: 'intelliSortTable.team',
        accessor: 'team',
        minWidth: 100,
        sortable: false,
        filterable: false,
        show: false,
        Cell: props => {
            const {
                tdProps: {
                    rest: { selectedTeamId }
                }
            } = props;
            return <span className={theme.cellText}>{selectedTeamId}</span>;
        }
    }
];

const BulkMarkAccurateErrorsModal = ({ t, theme, errors, onClose }) => {
    return (
        <div className={theme.bulkEditErrorsModal}>
            <span className={theme.title}>
                {t('bulkMarkAccurate.updatesFailed', { count: errors.length })}
            </span>
            <Button
                buttonType={ButtonTypes.Icon}
                circle
                icon="cancel"
                className={classnames(theme.button, theme.closeIcon)}
                theme={theme}
                onClick={onClose}
            />
            <div className={theme.errorsList}>
                {errors.map((error, index) => {
                    return (
                        <span key={index} className={theme.error}>
                            <ErrorIcon visible theme={theme} /> {error}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type IntelliSortRequestDataType = {
    autosortOrders: Array<IntelliSortRequest>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    theme: Object,
    data: IntelliSortRequestDataType,
    unitConverter: UnitConversionHelper,
    entitySettings: ?EntitySettings,
    tableSettings: ?Array<Object>,
    t: TFunction,
    fetchOptions: () => {},
    isGlobalPersonnel: boolean
};

const ENTITY_NAME_ID = 'masterIntelliSort';

const intelliSortTableContainerFactory = ({
    COLUMNS = DEFAULT_COLUMNS,
    options = {
        entityTableId: ENTITY_TABLE_ID
    },
    BulkViewEmptyState = IntelliSortEmptyState,
    fetchEntities = IntelliSortActions.fetchActiveEntities,
    ActionsBar = IntelliSortActionsBar
}) => {
    const IntelliSortTable = entityMasterFactory({
        COLUMNS,
        options,
        BulkViewEmptyState,
        ActionsBar
    });
    class IntelliSortTableContainer extends PureComponent<Props> {
        constructor(props) {
            super(props);
            this.state = {
                showBulkMarkAccurateConfirmation: false,
                showBulkMarkAccurateProgress: false,
                showBulkMarkAccurateErrors: false,
                bulkMarkAccurateErrors: []
            };
        }

        componentDidMount() {
            const { selectedTeamId, clientId } = this.props;
            if (selectedTeamId) {
                this.fetchHomebases(clientId, selectedTeamId);
            }
        }

        componentDidUpdate(prevProps, prevState) {
            const { selectedTeamId, clientId, selectedDate } = this.props;
            const { homebases } = this.state;
            if (selectedTeamId !== prevProps.selectedTeamId) {
                this.fetchHomebases(clientId, selectedTeamId);
                return;
            }
            if (
                JSON.stringify(homebases) !==
                    JSON.stringify(prevState.homebases) ||
                selectedDate !== prevProps.selectedDate
            ) {
                this.masterContainer.reloadData();
            }
        }

        checkFeedbackActive = (selectedDate, autosortSettings) =>
            TimeUtils.daysFromNow(TimeUtils.getMoment(selectedDate)) <
            autosortSettings.zoneFeedbackPeriod;

        getTdProps = (state, rowInfo) => {
            if (!rowInfo) {
                return {};
            }
            const {
                original: { id }
            } = rowInfo;
            const {
                t,
                entitySettings,
                autosortSettings,
                selectedRows,
                toggleRowSelection,
                unitConverter,
                theme,
                isGlobalPersonnel,
                selectedDate,
                data,
                selectedTeamId
            } = this.props;
            const isFeedbackActive = this.checkFeedbackActive(
                selectedDate,
                autosortSettings
            );
            const isChecked = selectedRows.indexOf(id) >= 0;
            return {
                onCheckboxClick: () => {
                    toggleRowSelection(id);
                },
                t,
                entitySettings,
                unitConverter,
                theme,
                isChecked,
                className: classnames({ [theme.selectedRow]: isChecked }),
                isGlobalPersonnel,
                handleFeedback: this.handleFeedback,
                isFeedbackActive,
                data,
                selectedTeamId
            };
        };

        getHomebaseIds = homebases => {
            return homebases
                ? homebases.map(homebase => homebase.id).toList()
                : null;
        };

        getMasterContainerRef = ref => {
            this.masterContainer = ref;
        };

        handleFeedback = (
            showSuccessMessage,
            shouldReloadData,
            fieldId,
            feedbackStatus,
            value,
            accessor
        ) => {
            const { updateFeedback, clientId } = this.props;
            const params = { clientId, ['intellisortId']: fieldId };
            return (
                updateFeedback &&
                updateFeedback(
                    showSuccessMessage,
                    params,
                    feedbackStatus,
                    value,
                    accessor
                ).then(() => {
                    shouldReloadData && this.masterContainer.reloadData();
                })
            );
        };

        showBulkMarkAccurateProgress = () => {
            this.setState({
                showBulkMarkAccurateProgress: true
            });
        };

        hideBulkMarkAccurateProgress = () => {
            this.setState({ showBulkMarkAccurateProgress: false });
        };

        showBulkMarkAccurateErrors = () => {
            this.setState({
                showBulkMarkAccurateErrors: true
            });
        };

        hideBulkMarkAccurateErrors = () => {
            this.setState({
                showBulkMarkAccurateErrors: false
            });
        };

        handleBulkMarkAccurate = () => {
            this.setState({
                needsBulkMarkAccurateConfirmation: true,
                bulkMarkAccurateAcceptCallback: () => {
                    this.showBulkMarkAccurateProgress();
                    this.applyMarkAllAccurate();
                }
            });
        };

        applyMarkAllAccurate = () => {
            const promisesToProcess = {};
            this.props.selectedRows.forEach(intellisortId => {
                promisesToProcess[intellisortId] = this.handleFeedback(
                    false,
                    false,
                    intellisortId,
                    'ACCEPTED'
                );
            });
            this.processBulkMarkAccuratePromises(promisesToProcess, {
                onRetry: this.applyMarkAllAccurate.bind(this)
            });
        };

        handleBulkMarkAccurateDismiss = () => {
            this.setState({
                needsBulkMarkAccurateConfirmation: false,
                bulkMarkAccurateAcceptCallback: null
            });
        };

        handleBulkMarkAccurateApproval = () => {
            const { bulkMarkAccurateAcceptCallback } = this.state;
            bulkMarkAccurateAcceptCallback && bulkMarkAccurateAcceptCallback();
            this.handleBulkMarkAccurateDismiss();
        };

        processBulkMarkAccuratePromises = (
            promisesToProcess: { [key: string]: Promise<*> },
            options: {
                onRetry: (Array<string>) => void
            }
        ) => {
            const { showSuccessToast, showErrorToast, t } = this.props;
            const { onRetry } = options;
            this.setState({
                bulkMarkAccurateErrors: [],
                showBulkMarkAccurateErrors: false
            });
            PromiseUtils.processAll(promisesToProcess).then(results => {
                const rejectedPromiseIds: Array<string> = [];
                results.forEach(result => {
                    if (result.status === PromiseStatus.REJECTED && result.id) {
                        rejectedPromiseIds.push(result.id);
                        this.setState(state => ({
                            bulkMarkAccurateErrors: [
                                ...state.bulkMarkAccurateErrors,
                                result.error.message
                            ]
                        }));
                    }
                });
                if (rejectedPromiseIds.length !== results.length) {
                    showSuccessToast({
                        message: t('bulkMarkAccurateToast', {
                            count: results.length - rejectedPromiseIds.length
                        }),
                        timeout: 5000
                    });
                }
                if (rejectedPromiseIds.length === 0) {
                    this.masterContainer.reloadData();
                    this.hideBulkMarkAccurateProgress();
                    return;
                }

                const errorContent = (
                    <div className={theme.errorToastContentWrap}>
                        <span className={theme.message}>
                            {t('bulkMarkAccurate.updatesFailed', {
                                count: rejectedPromiseIds.length
                            })}
                        </span>
                        <span
                            className={theme.viewErrors}
                            onClick={this.showBulkMarkAccurateErrors}
                        >
                            {t('bulkMarkAccurate.viewErrors')}
                        </span>
                    </div>
                );

                const actions = ({ hideToast }) => {
                    const tryAgain = () => {
                        onRetry(rejectedPromiseIds);
                        hideToast();
                    };

                    const cancel = () => {
                        hideToast();
                        this.hideBulkMarkAccurateProgress();
                    };
                    return (
                        <div className={theme.errorToastButtons}>
                            <Button
                                flat
                                semibold
                                uppercase
                                label={t('bulkMarkAccurate.tryAgain')}
                                color={dashboardColors.salmonTwo.name}
                                theme={theme}
                                buttonType={ButtonTypes.Text}
                                onClick={tryAgain} //eslint-disable-line react/jsx-no-bind
                            />
                            <Button
                                buttonType={ButtonTypes.Icon}
                                circle
                                icon="cancel"
                                className={classnames(
                                    theme.button,
                                    theme.closeIcon
                                )}
                                theme={theme}
                                onClick={cancel} //eslint-disable-line react/jsx-no-bind
                            />
                        </div>
                    );
                };
                showErrorToast({
                    customContent: errorContent,
                    customAction: actions,
                    timeout: 0
                });
            });
        };

        renderBulkProgress() {
            const { theme, t } = this.props;
            const { bulkMarkAccurateErrors } = this.state;
            return (
                <div className={theme.bulkUpdatesLoader}>
                    {bulkMarkAccurateErrors.length ? (
                        <div className={theme.failedErrorWrap}>
                            <span>{t('bulkMarkAccurate.failed')}</span>
                        </div>
                    ) : (
                        <Loader
                            theme={theme}
                            active
                            withText
                            title={t('updating')}
                        />
                    )}
                </div>
            );
        }

        fetchHomebases = () => {
            const { clientId, selectedTeamId } = this.props;
            fetchHomebasesApi
                .fetch({ clientId, team: selectedTeamId })
                .then(response => {
                    const homebases = Immutable.List(
                        response.map(homebase => Homebase.fromJS(homebase))
                    );
                    this.setState({ homebases });
                })
                .catch(() => {
                    this.setState({ homebases: null });
                });
        };

        render() {
            const {
                selectedRows,
                clientId,
                selectedDate,
                autosortSettings,
                fetchOptions,
                t,
                isZoneBasedRoutingMetadataHidden
            } = this.props;
            const {
                showBulkMarkAccurateProgress,
                showBulkMarkAccurateErrors,
                bulkMarkAccurateErrors,
                needsBulkMarkAccurateConfirmation,
                homebases
            } = this.state;
            const intellisortFetchOptions = {
                clientId,
                selectedDate,
                homebases: this.getHomebaseIds(homebases),
                isZoneBasedRoutingMetadataHidden,
                ...(fetchOptions || {})
            };
            let enableBulkMarkAccurate = false;
            if (
                selectedRows.length > 0 &&
                this.checkFeedbackActive(selectedDate, autosortSettings)
            ) {
                enableBulkMarkAccurate = true;
            }
            const customActions = {
                onBulkMarkAccurate: enableBulkMarkAccurate
                    ? this.handleBulkMarkAccurate
                    : null,
                selectedDate,
                homebases,
                clientId
            };
            const entityI18nId =
                selectedRows.length > 1
                    ? 'entities:masterIntelliSort_plural'
                    : 'entities:masterIntelliSort';
            return (
                <div className={theme.wrap}>
                    {showBulkMarkAccurateProgress
                        ? this.renderBulkProgress()
                        : null}
                    <IntelliSortTable
                        {...this.props}
                        customActions={customActions}
                        emptyStateImageSrc="/img/location-master-empty.png"
                        entityNameId={ENTITY_NAME_ID}
                        refProvider={this.getMasterContainerRef}
                        title={t('entities:intellisort')}
                        entitySettingsName={'intellisortSettings'}
                        fetchOptions={intellisortFetchOptions}
                        isEditEntityActive={false}
                        getTdProps={this.getTdProps}
                        hasFreezableColumns
                        frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                        pinTooltip={t('entityManagement:pinTooltip')}
                        unpinTooltip={t('entityManagement:unpinTooltip')}
                        shouldHideAddEntity
                        shouldHideEditEntity
                        shouldHideDeleteEntity
                    >
                        <Modal theme={theme}>
                            {showBulkMarkAccurateErrors ? (
                                <BulkMarkAccurateErrorsModal
                                    theme={theme}
                                    t={t}
                                    errors={bulkMarkAccurateErrors}
                                    onClose={this.hideBulkMarkAccurateErrors}
                                />
                            ) : null}
                        </Modal>
                        {needsBulkMarkAccurateConfirmation ? (
                            <BulkEditConfirmationDialog
                                selectionCount={selectedRows.length}
                                entityI18nId={entityI18nId}
                                onAccept={this.handleBulkMarkAccurateApproval}
                                onCancel={this.handleBulkMarkAccurateDismiss}
                            />
                        ) : (
                            []
                        )}
                    </IntelliSortTable>
                </div>
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const clientId = clientSelector(state, ownProps);
        const selectedTeamId = activeTeamIdSelector(state, ownProps);
        const selectedDate = selectedDateSelector(state, ownProps);
        const isLoading = IntelliSortSelectors.isEntitiesLoading(
            state,
            ownProps
        );
        const data = IntelliSortSelectors.getEntities(state, ownProps);
        const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
        const entityIdToEdit = editEntityIdSelector(state, ownProps);
        const entitySettings = getEntitySettings(state, ownProps);
        const autosortSettings = getAutoSortSettings(state, ownProps);
        const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
        const isImportEntityActive = isImportEntityActiveSelector(
            state,
            ownProps
        );
        const isExportEntityActive = isExportEntityActiveSelector(
            state,
            ownProps
        );
        const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);
        const isZoneBasedRoutingMetadataHidden = !autosortSettings.showZoneBasedRoutingMetadata;

        return {
            clientId,
            selectedTeamId,
            selectedDate,
            isLoading,
            data,
            isAddEntityActive,
            entityIdToEdit,
            entitySettings,
            autosortSettings,
            tableSettings,
            isImportEntityActive,
            isExportEntityActive,
            isGlobalPersonnel,
            isZoneBasedRoutingMetadataHidden
        };
    };

    const withRedux = connect(mapStateToProps, {
        fetchEntities,
        showToastAction: ToastActions.showToast,
        updateToastAction: ToastActions.updateToast,
        hideToastAction: ToastActions.hideToast,
        disableEntity: IntelliSortActions.disableEntity,
        clearData: IntelliSortActions.clearData,
        saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
        updateFeedback: IntelliSortActions.updateFeedback,
        showSuccessToast: ToastActions.showSuccessToast,
        showErrorToast: ToastActions.showErrorToast
    });

    const enhance = compose(
        themr(null, theme),
        themr(null, specificTheme),
        withRouter,
        withUnitConverter,
        withRedux,
        translate('intelliSort'),
        MasterContainerHOC
    );
    return enhance(IntelliSortTableContainer);
};

const ThemedConnectedIntelliSortTableContainer = intelliSortTableContainerFactory(
    {}
);

export {
    intelliSortTableContainerFactory,
    ThemedConnectedIntelliSortTableContainer as IntelliSortTableContainer
};
export default ThemedConnectedIntelliSortTableContainer;
